import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import './DimensionGate.css';

// ADDED: tesseract edge pairs — 16 vertices of a 4D hypercube projected to 3D
function buildTesseract(scale = 0.4) {
  const verts4D = [];
  for (let i = 0; i < 16; i++) {
    verts4D.push([
      (i & 1 ? 1 : -1),
      (i & 2 ? 1 : -1),
      (i & 4 ? 1 : -1),
      (i & 8 ? 1 : -1),
    ]);
  }
  const edges = [];
  for (let i = 0; i < 16; i++) {
    for (let j = i + 1; j < 16; j++) {
      let diff = 0;
      for (let k = 0; k < 4; k++) if (verts4D[i][k] !== verts4D[j][k]) diff++;
      if (diff === 1) edges.push([i, j]);
    }
  }
  return { verts4D, edges, scale };
}

function project4Dto3D(v4, angle1, angle2) {
  // Rotate in the XW plane
  const cosA = Math.cos(angle1), sinA = Math.sin(angle1);
  const x1 = v4[0] * cosA - v4[3] * sinA;
  const w1 = v4[0] * sinA + v4[3] * cosA;
  // Rotate in the ZW plane
  const cosB = Math.cos(angle2), sinB = Math.sin(angle2);
  const z1 = v4[2] * cosB - w1 * sinB;
  const w2 = v4[2] * sinB + w1 * cosB;
  // Perspective project from 4D to 3D
  const wDist = 2.5;
  const f = wDist / (wDist - w2);
  return new THREE.Vector3(x1 * f, v4[1] * f, z1 * f);
}

export default function DimensionGate({ onEnter }) {
  const [showButton, setShowButton] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const shatterRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setShowButton(true), 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    const isMobile = window.innerWidth < 768;
    let W = (canvas.width = window.innerWidth);
    let H = (canvas.height = window.innerHeight);

    let starAnimId, cubeAnimId, particleAnimId;
    let cube, tesseract, renderer, scene, camera;
    let pGeo = null;
    let cubeReady = false;
    // ADDED: tesseract rotation angles
    let angle1 = 0, angle2 = 0;

    // --- STARFIELD ---
    const NUM_STARS = isMobile ? 400 : 800;
    const stars = Array.from({ length: NUM_STARS }, () => ({
      x: Math.random() * W - W / 2,
      y: Math.random() * H - H / 2,
      z: Math.random() * W,
    }));

    function drawStars() {
      ctx.fillStyle = 'rgba(0,0,0,0.12)';
      ctx.fillRect(0, 0, W, H);
      for (const s of stars) {
        const sc = W / s.z;
        const x = W / 2 + s.x * sc;
        const y = H / 2 + s.y * sc;
        const r = Math.max(0.4, 1.2 / s.z);
        const alpha = Math.min(1, (1 - s.z / W) * 1.5);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(2)})`;
        ctx.fill();
        s.z -= 1.2;
        if (s.z <= 0) {
          s.x = Math.random() * W - W / 2;
          s.y = Math.random() * H - H / 2;
          s.z = W;
        }
      }
      starAnimId = requestAnimationFrame(drawStars);
    }
    starAnimId = requestAnimationFrame(drawStars);

    // --- THREE.JS SCENE ---
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(60, W / H, 0.1, 1000);
    camera.position.z = 4;

    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    while (container.firstChild) container.removeChild(container.firstChild);
    container.appendChild(renderer.domElement);

    // ADDED: build tesseract as a LineSegments object
    const { verts4D, edges, scale } = buildTesseract(2.2);
    const tessPositions = new Float32Array(edges.length * 6);
    const tessGeo = new THREE.BufferGeometry();
    tessGeo.setAttribute('position', new THREE.BufferAttribute(tessPositions, 3));
    const tessMat = new THREE.LineBasicMaterial({
      color: 0xdde8f0,       // CHANGED: white/silver
      transparent: true,
      opacity: 0.45,
    });
    tesseract = new THREE.LineSegments(tessGeo, tessMat);
    scene.add(tesseract);

    // Texture cube
    const loader = new THREE.TextureLoader();
    loader.load('/images/abstract-glitter.gif', (texture) => {
      const geometry = new THREE.BoxGeometry(1.5, 1.5, 1.5);
      const material = new THREE.MeshBasicMaterial({ map: texture });
      cube = new THREE.Mesh(geometry, material);
      scene.add(cube);
      cubeReady = true;
    });

    // ADDED: update tesseract vertex positions each frame
    function updateTesseract() {
      angle1 += 0.004;
      angle2 += 0.003;
      const pos = tessGeo.attributes.position.array;
      edges.forEach(([i, j], idx) => {
        const a = project4Dto3D(verts4D[i], angle1, angle2);
        const b = project4Dto3D(verts4D[j], angle1, angle2);
        const s = scale / 2.2; // normalize to scene units
        pos[idx * 6 + 0] = a.x * s;
        pos[idx * 6 + 1] = a.y * s;
        pos[idx * 6 + 2] = a.z * s;
        pos[idx * 6 + 3] = b.x * s;
        pos[idx * 6 + 4] = b.y * s;
        pos[idx * 6 + 5] = b.z * s;
      });
      tessGeo.attributes.position.needsUpdate = true;
    }

    // --- SHATTER ---
    function createShatter() {
      if (!cubeReady) return;
      scene.remove(cube);
      scene.remove(tesseract);
      cube.geometry.dispose();
      cube.material.dispose();
      tessGeo.dispose();

      const COUNT = isMobile ? 150 : 400;
      const positions = new Float32Array(COUNT * 3);
      const velocities = [];

      for (let i = 0; i < COUNT; i++) {
        const face = Math.floor(Math.random() * 6);
        const h = 0.75;
        const u = (Math.random() - 0.5) * 1.5;
        const v = (Math.random() - 0.5) * 1.5;
        let px, py, pz;
        switch (face) {
          case 0:  px = h;  py = u;  pz = v;  break;
          case 1:  px = -h; py = u;  pz = v;  break;
          case 2:  px = u;  py = h;  pz = v;  break;
          case 3:  px = u;  py = -h; pz = v;  break;
          case 4:  px = u;  py = v;  pz = h;  break;
          default: px = u;  py = v;  pz = -h; break;
        }
        positions[i * 3]     = px;
        positions[i * 3 + 1] = py;
        positions[i * 3 + 2] = pz;
        const len = Math.sqrt(px * px + py * py + pz * pz) || 1;
        const speed = 2 + Math.random() * 4;
        velocities.push({
          x: (px / len) * speed + (Math.random() - 0.5) * 0.8,
          y: (py / len) * speed + (Math.random() - 0.5) * 0.8,
          z: (pz / len) * speed,
        });
      }

      pGeo = new THREE.BufferGeometry();
      pGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const pMat = new THREE.PointsMaterial({
        color: 0xdde8f0,
        size: isMobile ? 0.06 : 0.04,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      scene.add(new THREE.Points(pGeo, pMat));

      let lastTime = performance.now();
      function animParticles(now) {
        const dt = Math.min((now - lastTime) / 1000, 0.05);
        lastTime = now;
        const pos = pGeo.attributes.position.array;
        for (let i = 0; i < COUNT; i++) {
          pos[i * 3]     += velocities[i].x * dt;
          pos[i * 3 + 1] += velocities[i].y * dt;
          pos[i * 3 + 2] += velocities[i].z * dt;
        }
        pGeo.attributes.position.needsUpdate = true;
        pMat.opacity = Math.max(0, pMat.opacity - dt * 1.5);
        renderer.render(scene, camera);
        if (pMat.opacity > 0) {
          particleAnimId = requestAnimationFrame(animParticles);
        }
      }
      particleAnimId = requestAnimationFrame(animParticles);
    }

    // --- CUBE + TESSERACT ANIMATION LOOP ---
    function animCube() {
      if (!cubeReady) {
        updateTesseract();
        renderer.render(scene, camera);
        cubeAnimId = requestAnimationFrame(animCube);
        return;
      }
      if (shatterRef.current) {
        createShatter();
        return;
      }
      cube.rotation.y += 0.006;
      cube.rotation.x += 0.003;
      updateTesseract(); // ADDED: tesseract updates every frame
      renderer.render(scene, camera);
      cubeAnimId = requestAnimationFrame(animCube);
    }
    cubeAnimId = requestAnimationFrame(animCube);

    return () => {
      cancelAnimationFrame(starAnimId);
      cancelAnimationFrame(cubeAnimId);
      cancelAnimationFrame(particleAnimId);
      pGeo?.dispose();
      tessGeo?.dispose();
      renderer?.dispose();
      while (container.firstChild) container.removeChild(container.firstChild);
    };
  }, []);

  const handleEnter = () => {
    shatterRef.current = true;
    setIsExiting(true);
    document.body.classList.add('dimension-entering');
    setTimeout(() => {
      onEnter();
      setTimeout(() => document.body.classList.remove('dimension-entering'), 1500);
    }, 900);
  };

  return (
    <div className={`dimension-gate${isExiting ? ' gate-exiting' : ''}`}>
      <canvas ref={canvasRef} className="gate-starfield" />
      <div ref={containerRef} className="gate-cube-container" />
      {/* ADDED: title fades in after 1s */}
      <h1 className="gate-title">Vanessa Victorino</h1>
      {showButton && !isExiting && (
        <button className="gate-enter-btn" onClick={handleEnter}>
          Enter
        </button>
      )}
    </div>
  );
}